import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { FlowInstanceEventNotification } from '@@components';

import { ActivityMetadataModel, FlowInstanceStatus, StepType } from '../../../api/api';
import { FlowEdge, FlowNode } from '../Models/GraphModel';
import { getLatestFlowActivityEvent } from '../Services/getFlowInstanceLiveState';
import {
	ExecutionTraceStep,
	buildExecutionTrace,
	getGraphOverlaySteps,
	isFinishedFlowInstanceStatus,
	startsNewWaitingRun,
} from '../Services/FlowExecutionTraceService';

const LIVE_TRACE_LIMIT = 200;
const EMPTY_TRACE_STEPS: ExecutionTraceStep[] = [];

export type { ExecutionTraceStep } from '../Services/FlowExecutionTraceService';

type UseFlowExecutionTraceArgs = {
	selectedInstanceId: string | null;
	flowInstanceEvents: FlowInstanceEventNotification[];
	edges: FlowEdge[];
	nodes: FlowNode[];
	activityByName: Map<string, ActivityMetadataModel>;
	selectedInstanceStatus?: FlowInstanceStatus;
	selectedInstanceCurrentActivityId?: string;
};

export function useFlowExecutionTrace({
	selectedInstanceId,
	flowInstanceEvents,
	edges,
	nodes,
	activityByName,
	selectedInstanceStatus,
	selectedInstanceCurrentActivityId,
}: UseFlowExecutionTraceArgs) {
	const [persistedTrace, setPersistedTrace] = useState<{ instanceId: string | null; steps: ExecutionTraceStep[] }>({ instanceId: null, steps: [] });
	const persistedRunSteps = persistedTrace.instanceId === selectedInstanceId ? persistedTrace.steps : EMPTY_TRACE_STEPS;
	const setHistoricalExecutionSteps = useCallback(
		(steps: ExecutionTraceStep[]) => {
			setPersistedTrace({ instanceId: selectedInstanceId, steps });
		},
		[selectedInstanceId],
	);
	const [signalRTrace, setSignalRTrace] = useState<{ instanceId: string | null; steps: ExecutionTraceStep[] }>({ instanceId: null, steps: [] });
	const signalRSteps = signalRTrace.instanceId === selectedInstanceId ? signalRTrace.steps : EMPTY_TRACE_STEPS;
	const previousSelectedInstanceIdRef = useRef<string | null>(null);
	const processedEventsRef = useRef(new WeakSet<FlowInstanceEventNotification>());

	const clearExecutionTraceSteps = useCallback(() => {
		setPersistedTrace((current) => (current.steps.length === 0 ? current : { ...current, steps: [] }));
		setSignalRTrace((current) => (current.steps.length === 0 ? current : { ...current, steps: [] }));
	}, []);

	useEffect(() => {
		if (previousSelectedInstanceIdRef.current !== selectedInstanceId) {
			previousSelectedInstanceIdRef.current = selectedInstanceId;
			processedEventsRef.current = new WeakSet();
			setSignalRTrace({ instanceId: selectedInstanceId, steps: [] });
		}
	}, [clearExecutionTraceSteps, selectedInstanceId]);

	const executionSteps = useMemo(() => {
		return getGraphOverlaySteps({
			persistedRunSteps,
			signalRSteps,
			edges,
			selectedInstanceStatus,
			selectedInstanceCurrentActivityId,
		});
	}, [edges, persistedRunSteps, selectedInstanceCurrentActivityId, selectedInstanceStatus, signalRSteps]);

	const selectedInstanceHasFinished = isFinishedFlowInstanceStatus(selectedInstanceStatus);
	const latestActivityEvent = getLatestFlowActivityEvent(flowInstanceEvents, selectedInstanceId);
	// State-only progress reports control returning to an enclosing block. Trace
	// OnLeave events still identify the completed child and must retain that identity.
	const reportedCurrentActivityId = latestActivityEvent?.payload.executionId && !latestActivityEvent.payload.stepType && !latestActivityEvent.payload.status
		? latestActivityEvent.payload.currentActivityId ?? null : undefined;
	const executionTrace = useMemo(
		() => buildExecutionTrace(
			executionSteps,
			edges,
			nodes,
			activityByName,
			selectedInstanceHasFinished,
			selectedInstanceCurrentActivityId,
			selectedInstanceStatus === FlowInstanceStatus.Waiting,
			reportedCurrentActivityId,
		),
		[activityByName, edges, executionSteps, nodes, reportedCurrentActivityId, selectedInstanceCurrentActivityId, selectedInstanceHasFinished, selectedInstanceStatus],
	);

	useEffect(() => {
		if (!selectedInstanceId) {
			return;
		}
		const pending = flowInstanceEvents.filter((event) => event.payload.id === selectedInstanceId && !processedEventsRef.current.has(event)).reverse();
		pending.forEach((event) => processedEventsRef.current.add(event));
		if (!pending.some((event) => event.payload.currentActivityId && event.payload.stepType)) {
			return;
		}
		setSignalRTrace((current) => {
			let result = current.instanceId === selectedInstanceId ? current.steps : [];
			for (const event of pending) {
				const eventPayload = event.payload;
				if (!eventPayload.currentActivityId || !eventPayload.stepType) {
					continue;
				}
				const newStep: ExecutionTraceStep = {
					activityId: eventPayload.currentActivityId!,
					executionId: eventPayload.executionId ?? eventPayload.id,
					runId: eventPayload.runId,
					parentActivityId: eventPayload.parentActivityId,
					name: eventPayload.name,
					timestamp: eventPayload.occurredAt || event.receivedAt,
					type: eventPayload.stepType as StepType,
					order: 0,
				};

				const previous = result[result.length - 1];
				const newRun = newStep.runId && previous?.runId ? newStep.runId !== previous.runId : startsNewWaitingRun(newStep, previous, selectedInstanceStatus);
				const currentRunSteps = newRun ? [] : result;

				result = [
					...currentRunSteps,
					{
						...newStep,
						order: (currentRunSteps[currentRunSteps.length - 1]?.order ?? -1) + 1,
					},
				].slice(-LIVE_TRACE_LIMIT);
			}
			return { instanceId: selectedInstanceId, steps: result };
		});
	}, [flowInstanceEvents, selectedInstanceId, selectedInstanceStatus]);

	return {
		executionTrace,
		setHistoricalExecutionSteps,
		clearExecutionTraceSteps,
	};
}
